import React from 'react';
import { Card } from '@/components/ui/Card';
import { useLanguage } from '@/state/language/LanguageProvider';
import { UserNavigationConfig } from '@/components/navigation/UserNavigationConfig';

interface PreferencesTabProps {
  active: boolean;
  formData: {
    themePreference: string;
    language: string;
    timezone: string;
    dateFormat: string;
    timeFormat: string;
    currency: string;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
      inApp: boolean;
    };
    dashboardLayout: string;
    compactMode: boolean;
    autoSave: boolean;
    accessibility: {
      highContrast: boolean;
      fontSize: string;
      screenReader: boolean;
    };
  };
  availableThemes: Array<{ id: string; name: string }>;
  availableLanguages: Array<{ id: string; nativeName: string; flag: string }>;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleNotificationChange: (type: string, checked: boolean) => void;
  handleAccessibilityChange: (setting: string, value: string | boolean) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PreferencesTab: React.FC<PreferencesTabProps> = ({
  active,
  formData,
  availableThemes,
  availableLanguages,
  handleSelectChange,
  handleNotificationChange,
  handleAccessibilityChange,
  handleInputChange,
}) => {
  const { t } = useLanguage();

  if (!active) {
    return null;
  }

  return (
    <>
      {/* Main Preferences Card */}
      <div className="">
        <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
          <div className="space-y-6">
            {/* Theme & Language */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('preferences.theme')}
                </label>
                <select
                  name="themePreference"
                  value={formData.themePreference}
                  onChange={handleSelectChange}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                >
                  {availableThemes.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {theme.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('preferences.language')}
                </label>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleSelectChange}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                >
                  {availableLanguages.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.flag} {lang.nativeName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timezone & Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('preferences.timezone')}
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleSelectChange}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">{t('preferences.easternTime')}</option>
                  <option value="America/Chicago">{t('preferences.centralTime')}</option>
                  <option value="America/Denver">{t('preferences.mountainTime')}</option>
                  <option value="America/Los_Angeles">{t('preferences.pacificTime')}</option>
                  <option value="Europe/London">{t('preferences.london')}</option>
                  <option value="Europe/Paris">{t('preferences.paris')}</option>
                  <option value="Asia/Tokyo">{t('preferences.tokyo')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('preferences.currency')}
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleSelectChange}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                >
                  <option value="USD">{t('preferences.usd')}</option>
                  <option value="EUR">{t('preferences.eur')}</option>
                  <option value="GBP">{t('preferences.gbp')}</option>
                  <option value="JPY">{t('preferences.jpy')}</option>
                  <option value="CAD">{t('preferences.cad')}</option>
                </select>
              </div>
            </div>

            {/* Date and Time Format */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('preferences.dateFormat')}
                </label>
                <select
                  name="dateFormat"
                  value={formData.dateFormat}
                  onChange={handleSelectChange}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('preferences.timeFormat')}
                </label>
                <select
                  name="timeFormat"
                  value={formData.timeFormat}
                  onChange={handleSelectChange}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                >
                  <option value="12h">{t('preferences.12h')}</option>
                  <option value="24h">{t('preferences.24h')}</option>
                </select>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Additional Preferences Sections */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notification Preferences */}
        <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
            {t('preferences.notifications')}
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.emailNotifications')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.receiveEmailNotifications')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.notifications.email}
                onChange={(e) => handleNotificationChange('email', e.target.checked)}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.pushNotifications')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.receivePushNotifications')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.notifications.push}
                onChange={(e) => handleNotificationChange('push', e.target.checked)}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.smsNotifications')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.receiveSmsNotifications')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.notifications.sms}
                onChange={(e) => handleNotificationChange('sms', e.target.checked)}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.inAppNotifications')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.showInAppNotifications')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.notifications.inApp}
                onChange={(e) => handleNotificationChange('inApp', e.target.checked)}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>
          </div>
        </Card>

        {/* Accessibility Preferences */}
        <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
            {t('preferences.accessibility')}
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.highContrast')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.useHighContrastColors')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.accessibility.highContrast}
                onChange={(e) => handleAccessibilityChange('highContrast', e.target.checked)}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                {t('preferences.fontSize')}
              </label>
              <select
                name="accessibility_fontSize"
                value={formData.accessibility.fontSize}
                onChange={handleSelectChange}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
              >
                <option value="small">{t('preferences.small')}</option>
                <option value="medium">{t('preferences.medium')}</option>
                <option value="large">{t('preferences.large')}</option>
                <option value="xlarge">{t('preferences.xlarge')}</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.screenReader')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.enableScreenReaderSupport')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.accessibility.screenReader}
                onChange={(e) => handleAccessibilityChange('screenReader', e.target.checked)}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>
          </div>
        </Card>

        {/* Interface Preferences */}
        <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
            {t('preferences.interface')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                {t('preferences.dashboardLayout')}
              </label>
              <select
                name="dashboardLayout"
                value={formData.dashboardLayout}
                onChange={handleSelectChange}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
              >
                <option value="grid">
                  {t('preferences.grid')} {t('common.layout')}
                </option>
                <option value="list">
                  {t('preferences.list')} {t('common.layout')}
                </option>
                <option value="compact">
                  {t('preferences.compact')} {t('common.layout')}
                </option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.compactMode')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.useCompactInterface')}
                </p>
              </div>
              <input
                type="checkbox"
                name="compactMode"
                checked={formData.compactMode}
                onChange={handleInputChange}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[var(--color-surface-text)]">
                  {t('preferences.autoSave')}
                </label>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('preferences.automaticallySaveChanges')}
                </p>
              </div>
              <input
                type="checkbox"
                name="autoSave"
                checked={formData.autoSave}
                onChange={handleInputChange}
                className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Navigation Configuration Section */}
      <div className="mt-6">
        <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
            {t('navigation.configuration')}
          </h2>
          <UserNavigationConfig />
        </Card>
      </div>
    </>
  );
};

